import React from 'react';
import { useMutation } from '@apollo/client';
import Card, { Cards } from '../components/Card';
import FormModal from '../components/FormModal';
import ReorderableList from '../components/ReorderableList';
import SelectEntityOption from '../components/SelectEntityOption';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import { TopicEntityIcon } from '../topic/TopicIcon';
import nonEmpty from '../utils/nonEmpty';
import * as styles from './ReorderTopicsModal.module.scss';

const TOPICS_FRAGMENT = gql(`
  fragment ReorderTopicsModal_topics on Topic {
    id
    name
  }
`);

const PARENT_TOPIC_FRAGMENT = gql(`
  fragment ReorderTopicsModal_parentTopic on Topic {
    id
    name
    ancestors {
      id
      ...TopicBreadcrumbs_topics
    }
  }
`);

const REORDER_TOPICS_MUTATION = gql(/** @complexity 4 */ `
  mutation ReorderTopics($parentId: ID, $topicIds: [ID!]!) {
    reorderTopics(parentId: $parentId, topicIds: $topicIds) {
      id
      order
    }
  }
`);

interface ReorderTopicsModalProps {
  topics: DocumentType<typeof TOPICS_FRAGMENT>[];
  parentTopic?: DocumentType<typeof PARENT_TOPIC_FRAGMENT>;
  close: () => void;
}

const ReorderTopicsModal = ({
  topics,
  parentTopic,
  close,
}: ReorderTopicsModalProps) => {
  const [reorderTopics] = useMutation(REORDER_TOPICS_MUTATION);

  const handleSubmit = async (topicIds: string[]) => {
    await reorderTopics({
      variables: { parentId: parentTopic?.id ?? undefined, topicIds },
    });
    close();
  };

  return (
    <FormModal
      variant="expanded"
      title={parentTopic ? 'Reorder subtopics' : 'Reorder All Topics'}
      description={
        parentTopic
          ? 'Their position will change in the sidebar and topic pages for everyone'
          : 'Their position will change in the sidebar for everyone'
      }
      close={close}
      form={{
        onSubmit: (values) =>
          handleSubmit(values.items.map((item) => item.id).filter(nonEmpty)),
        initialValues: { items: topics },
        validateOnBlur: false,
        validateOnChange: false,
      }}
      footer={{
        submitButton: {
          text: 'Save',
        },
      }}
    >
      {({ values, setFieldValue }) => (
        <Cards>
          <Card noPadding>
            <div className={styles.header}>
              <SelectEntityOption
                icon={<TopicEntityIcon />}
                secondaryTop={
                  <TopicBreadcrumbs topics={parentTopic?.ancestors ?? []} />
                }
                label={parentTopic?.name ?? 'All Topics'}
              />
            </div>
          </Card>
          <ReorderableList
            onChange={(value) => setFieldValue('items', value)}
            items={values.items}
            renderItem={(item, index: number) => (
              <div className={styles.item}>
                <div className={styles.itemIcon}>{index + 1}</div>
                <div className={styles.itemName}>{item.name}</div>
              </div>
            )}
          />
        </Cards>
      )}
    </FormModal>
  );
};

export default ReorderTopicsModal;
